import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import styles from "../../styles/Components/mini-app/MiniLatestSignals.module.css";
import { HiMiniSignal } from "react-icons/hi2";
import { FaRegClock } from "react-icons/fa6";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { Skeleton, Tooltip } from "antd";
import useFetchWatchlists from "../../hooks/api/useFetchWatchlists";
import useDashboardSignalAutoRefresh from "../../hooks/api/useDashboardSignalAutoRefresh";
import useFetchSignalInfo from "../../hooks/api/useFetchSignalInfo";
import { useRelativeTime } from "../../hooks/useRelativeTime";

const SignalItem = ({ signal }) => {
  const {
    symbol,
    trade_type: type,
    signal_date: date,
    tp1_percent,
    tp3_percent,
    tp5_percent,
    sl_percent,
    leverage,
  } = signal;
  const signalDate = useRelativeTime(date);

  const positionSize = useMemo(() => {
    const sl = parseFloat(sl_percent);
    const lev = parseFloat(leverage);
    if (!sl || !lev) return null;
    return Math.min(((1 / sl) * 100) / lev, 100).toFixed(2);
  }, [sl_percent, leverage]);

  return (
    <div className={styles.signalItem}>
      <div className={styles.signalLeft}>
        <div className={styles.signalLogo}>
          <LazyLoadImage
            src={`https://cryptocrispypro.s3.ap-southeast-2.amazonaws.com/symbol_icons/${symbol?.replace("/USDT", "")}.png`}
            alt={symbol}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "/placeholders/asset-logo.svg";
            }}
            width="32px"
            height="32px"
            effect="blur"
          />
        </div>
        <div className={styles.signalInfo}>
          <div className={styles.symbolRow}>
            <span className={styles.symbolName}>
              {symbol}
              {symbol?.includes("/USDT") ? "" : "/USDT"}
            </span>
            <span
              className={`${styles.typeBadge} ${type?.toLowerCase() === "buy" ? styles.buy : styles.sell}`}
            >
              {type} / {type?.toLowerCase() === "buy" ? "LONG" : "SHORT"}
            </span>
          </div>
          <div className={styles.signalMetrics}>
            <span className={styles.metric}>
              <span className={styles.metricLabel}>TP1:</span>
              <span className={styles.metricValue}>
                {parseFloat(tp1_percent).toFixed(2)}%
              </span>
            </span>
            <span className={styles.metric}>
              <span className={styles.metricLabel}>TP3:</span>
              <span className={styles.metricValue}>
                {parseFloat(tp3_percent).toFixed(2)}%
              </span>
            </span>
            <span className={styles.metric}>
              <span className={styles.metricLabel}>TP5:</span>
              <span className={styles.metricValue}>
                {parseFloat(tp5_percent).toFixed(2)}%
              </span>
            </span>
            <span className={styles.metric}>
              <span className={styles.metricLabel}>SL:</span>
              <span className={styles.metricValue}>
                {parseFloat(sl_percent).toFixed(2)}%
              </span>
            </span>
            {positionSize && (
              <span className={styles.metric}>
                <span className={styles.metricLabel}>
                  <Tooltip title="Position Size" color="#0c4cfc">
                    PS:
                  </Tooltip>
                </span>
                <span className={styles.metricValue}>{positionSize}%</span>
              </span>
            )}
          </div>
          <div className={styles.timeRow}>
            <FaRegClock size={12} />
            <span>{signalDate}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

function MiniLatestSignals() {
  const {
    dashboard: {
      signalInfoStatus,
      signalInfo: { signals = [] },
    },
  } = useSelector((state) => state);

  // Reusing logic from LatestSignals.js
  useFetchSignalInfo();
  useDashboardSignalAutoRefresh();
  useFetchWatchlists();

  const sortedSignals = [...signals]
    ?.sort((a, b) => new Date(b?.signal_date) - new Date(a?.signal_date))
    .slice(0, 10); // Limit to latest 10 for mini-app

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleGroup}>
          <HiMiniSignal className={styles.titleIcon} />
          <h3>Latest Signals</h3>
        </div>
      </div>

      <div className={styles.signalsList}>
        {signalInfoStatus === "loading"
          ? Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className={styles.loaderItem}>
                <Skeleton.Avatar active size="large" shape="circle" />
                <Skeleton active paragraph={{ rows: 1 }} title={false} />
              </div>
            ))
          : sortedSignals.map((signal) => (
              <SignalItem signal={signal} key={signal?.signal_date} />
            ))}
      </div>
    </div>
  );
}

export default MiniLatestSignals;
